import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { tipoOrganogramaService } from '@/services/api';
import { TipoOrganograma } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';

export const ORGANOGRAMA_TIPO_PRESETS: { value: string; label: string }[] = [
  { value: 'oficial', label: 'Oficial' },
  { value: 'planejamento', label: 'Planejamento' },
  { value: 'departamento', label: 'Departamento' },
  { value: 'projeto', label: 'Projeto' },
];

export const organogramaTipoLabel = (tipo: string): string => {
  const preset = ORGANOGRAMA_TIPO_PRESETS.find((p) => p.value === tipo);
  if (preset) return preset.label;
  if (tipo === 'customizado') return 'Customizado';
  return tipo;
};

const NOVO_TIPO_VALUE = '__novo_tipo__';

interface Props {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export function TipoOrganogramaSelect({ value, onChange, disabled }: Props) {
  const { user, empresaAtiva, empresaFiltroId } = useAuth();
  const [tipos, setTipos] = useState<TipoOrganograma[]>([]);
  const [novoTipoOpen, setNovoTipoOpen] = useState(false);
  const [novoTipoDescricao, setNovoTipoDescricao] = useState('');
  const [salvandoNovoTipo, setSalvandoNovoTipo] = useState(false);

  const empresaId = user?.tipo === 'superadmin' ? empresaFiltroId : empresaAtiva?.id;

  useEffect(() => {
    let ativo = true;
    tipoOrganogramaService.getAll(empresaId || undefined).then((res) => {
      if (ativo && res.success) setTipos(res.data.filter((t) => t.ativo));
    });
    return () => {
      ativo = false;
    };
  }, [empresaId]);

  const opcoesCustomizadas = tipos.filter(
    (t) => !ORGANOGRAMA_TIPO_PRESETS.some((p) => p.value.toLowerCase() === t.descricao.toLowerCase())
  );

  // Valor salvo que não está nem nos presets nem no catálogo (ex: dado antigo "customizado"
  // ou um tipo criado em outra empresa) — mostra como opção pra não sumir do select.
  const valorOrfao =
    value &&
    !ORGANOGRAMA_TIPO_PRESETS.some((p) => p.value === value) &&
    !opcoesCustomizadas.some((t) => t.descricao === value)
      ? value
      : null;

  const handleSelectChange = (novoValor: string) => {
    if (novoValor === NOVO_TIPO_VALUE) {
      setNovoTipoDescricao('');
      setNovoTipoOpen(true);
      return;
    }
    onChange(novoValor);
  };

  const handleCriarTipo = async () => {
    const descricao = novoTipoDescricao.trim();
    if (!descricao) {
      toast.error('Escreva o nome do tipo.');
      return;
    }

    setSalvandoNovoTipo(true);
    const res = await tipoOrganogramaService.create({ descricao, empresa_id: empresaId || undefined });
    setSalvandoNovoTipo(false);

    if (!res.success) {
      toast.error(res.error || 'Erro ao criar tipo');
      return;
    }

    setTipos((current) => [...current, res.data]);
    onChange(res.data.descricao);
    setNovoTipoOpen(false);
    toast.success('Tipo criado');
  };

  return (
    <>
      <Select value={value} onValueChange={handleSelectChange} disabled={disabled}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ORGANOGRAMA_TIPO_PRESETS.map((preset) => (
            <SelectItem key={preset.value} value={preset.value}>
              {preset.label}
            </SelectItem>
          ))}
          {valorOrfao && <SelectItem value={valorOrfao}>{organogramaTipoLabel(valorOrfao)}</SelectItem>}
          {opcoesCustomizadas.map((tipo) => (
            <SelectItem key={tipo.id} value={tipo.descricao}>
              {tipo.descricao}
            </SelectItem>
          ))}
          <SelectSeparator />
          <SelectItem value={NOVO_TIPO_VALUE} className="text-primary">
            <span className="flex items-center gap-2">
              <Plus className="h-4 w-4" /> Criar novo tipo
            </span>
          </SelectItem>
        </SelectContent>
      </Select>

      <Dialog open={novoTipoOpen} onOpenChange={setNovoTipoOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Novo tipo de organograma</DialogTitle>
            <DialogDescription>
              Fica disponível pra reutilizar em outros organogramas da empresa.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="novoTipoOrganograma">Nome do tipo</Label>
            <Input
              id="novoTipoOrganograma"
              value={novoTipoDescricao}
              onChange={(e) => setNovoTipoDescricao(e.target.value)}
              placeholder="Ex: Comercial"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCriarTipo();
              }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNovoTipoOpen(false)} disabled={salvandoNovoTipo}>
              Cancelar
            </Button>
            <Button onClick={handleCriarTipo} disabled={salvandoNovoTipo}>
              {salvandoNovoTipo ? 'Criando...' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
